import React, { PropsWithChildren, PureComponent } from 'react';
import { RouteComponentProps, matchPath, withRouter } from 'react-router';
import produce from 'immer';
import { PatientContext } from '../../../contexts/patient';
import { getPatientById } from '../../../apis/patient';
import { IPatient } from '../../../modules/unsw-fhir/Patient';
import {
  getBirthDate,
  getDisplayName,
  getFirstName,
  getLastName,
  getGender,
  getMrn,
  isActive,
} from '../../../selectors/patient';

type PatientProviderProps = PropsWithChildren<
  RouteComponentProps & { patientId: string | undefined | null }
>;

class PatientProvider extends PureComponent<
  PatientProviderProps,
  PatientContext & { patientId: string | undefined | null }
> {
  constructor(props: PatientProviderProps) {
    super(props);
    this.state = {
      loading: false,
      patientId: undefined,
      getDateOfBirth: this.getDateOfBirth,
      isActive: this.isActive,
      getGender: this.getGender,
      getMrn: this.getMrn,
      getFirstName: this.getFirstName,
      getLastName: this.getLastName,
      getDisplayName: this.getDisplayName,
      setPatient: this.setPatient,
      patient: undefined,
    };
  }

  componentDidUpdate = async () => {
    const patientId = this.props.patientId;

    if (
      !this.state.loading &&
      patientId &&
      patientId !== this.state.patientId
    ) {
      await this.updatePatient(patientId);
    }
  };

  componentDidMount = async () => {
    const patientId = this.props.patientId;

    if (
      !this.state.loading &&
      patientId &&
      patientId !== this.state.patientId
    ) {
      await this.updatePatient(patientId);
    }
  };

  render = () => (
    <PatientContext.Provider value={this.state}>
      {this.props.children}
    </PatientContext.Provider>
  );

  private getPatientId() {
    const match = matchPath<{
      organizationId: string;
      patientId: string;
    }>(this.props.location.pathname, {
      path: '/organization/:organizationId/patient/:patientId',
    });
    const patientId = match && match.params.patientId;
    return patientId;
  }

  private getDateOfBirth = () => getBirthDate(this.state.patient);

  private getGender = () => getGender(this.state.patient);

  private getMrn = () => getMrn(this.state.patient);

  private getDisplayName = () => getDisplayName(this.state.patient);

  private getFirstName = () => getFirstName(this.state.patient);

  private getLastName = () => getLastName(this.state.patient);

  private isActive = () => isActive(this.state.patient);

  private setPatient = (patient: IPatient | undefined) =>
    this.setState(state =>
      produce(state, draft => {
        draft.patientId = patient ? patient.id || null : null;
        draft.patient = patient;
        return draft;
      }),
    );

  private updatePatient = async (patientId: string) => {
    try {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = true;
          return draft;
        }),
      );
      const patient = await getPatientById(patientId);
      this.setState(state =>
        produce(state, draft => {
          draft.patientId = patientId;
          draft.patient = patient;
          draft.loading = false;
          return draft;
        }),
      );
    } catch (e) {
      this.setState(state =>
        produce(state, draft => {
          draft.loading = false;
          draft.error = e as string | undefined;
          return draft;
        }),
      );
    }
  };
}

export default withRouter(PatientProvider);
